import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClientOnly } from "@tanstack/react-router";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowLeftRight,
  ArrowUp,
  Link2,
  Loader2,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";

import { AdSlot } from "@/components/AdSlot";
import { CostChart } from "@/components/CostChart";
import { FeedbackLauncher } from "@/components/FeedbackLauncher";
import { PlaceInput, type PlaceValue } from "@/components/PlaceInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { NativeSelect } from "@/components/ui/native-select";
import { getVisitorId, track, usePageAnalytics } from "@/hooks/useAnalytics";
import logoMark from "@/assets/logo-mark.png";
import {
  VEHICLES,
  formatBrl,
  formatDuration,
  formatKm,
  legCost,
  type RouteResult,
  type TollPlazaHit,
  type VehicleType,
} from "@/lib/rota";
import { lazyWithRetry } from "@/lib/lazy-retry";

const RouteMap = lazyWithRetry(() => import("@/components/RouteMap"));

const SITE = "https://www.trajetofacil.com.br";

const OG_IMAGE = `${SITE}/og-trajetofacil.png`;

/** Limite de paradas por pesquisa (origem + destinos). */
const MAX_STOPS = 6;

type Stop = PlaceValue & { key: string };
type LegItem = { result: RouteResult; isReturn: boolean };

/** Parâmetros da pesquisa guardados na URL — permitem compartilhar o cálculo pronto. */
type HomeSearch = {
  r?: string | undefined;
  rt?: string | undefined;
  v?: string | undefined;
  ax?: string | undefined;
  pl?: string | undefined;
  kml?: string | undefined;
  mk?: string | undefined;
};


function str(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

let stopSeq = 0;
function newStop(address = "", placeId?: string): Stop {
  stopSeq += 1;
  return { key: `stop-${stopSeq}`, address, ...(placeId ? { placeId } : {}) };
}

function parseStops(raw: string | undefined): Stop[] {
  if (!raw) return [];
  return raw
    .split("|")
    .map((a) => a.trim())
    .filter(Boolean)
    .slice(0, MAX_STOPS)
    .map((a) => newStop(a));
}

function isVehicle(value: unknown): value is VehicleType {
  return VEHICLES.some((v) => v.value === value);
}

import { TITLE, DESCRIPTION, FAQ, HOW_TO_STEPS, CONTENT_LINKS } from "@/lib/home-content";





export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/` },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],

    links: [{ rel: "canonical", href: `${SITE}/` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "TrajetoFácil",
          url: `${SITE}/`,
          applicationCategory: "TravelApplication",
          operatingSystem: "Web",
          inLanguage: "pt-BR",
          description: DESCRIPTION,
          featureList: [
            "Cálculo de rota entre origem e destino",
            "Estimativa de pedágio por tipo de veículo e eixos",
            "Cálculo de combustível por preço do litro e km/l",
            "Ida e volta e repasse de custo por km",
          ],
          image: OG_IMAGE,
          offers: { "@type": "Offer", price: "0", priceCurrency: "BRL" },

        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "Como calcular uma rota com pedágio e combustível",
          totalTime: "PT1M",
          step: HOW_TO_STEPS.map((s, i) => ({
            "@type": "HowToStep",
            position: i + 1,
            name: s.name,
            text: s.text,
          })),
        }),
      },
    ],
  }),
  validateSearch: (raw: Record<string, unknown>): HomeSearch => ({
    r: str(raw["r"]),
    rt: str(raw["rt"]),
    v: str(raw["v"]),
    ax: str(raw["ax"]),
    pl: str(raw["pl"]),
    kml: str(raw["kml"]),
    mk: str(raw["mk"]),
  }),
  component: Index,

});


function Index() {
  usePageAnalytics();

  const search = Route.useSearch();
  const navigate = useNavigate();

  const [stops, setStops] = useState<Stop[]>(() => {
    const parsed = parseStops(search.r);
    return parsed.length >= 2 ? parsed : [newStop(), newStop()];
  });
  const [roundTrip, setRoundTrip] = useState(search.rt !== "0");
  const [vehicle, setVehicle] = useState<VehicleType>(isVehicle(search.v) ? search.v : "carro");
  const [axles, setAxles] = useState(() => {
    const n = Number(search.ax);
    return Number.isFinite(n) && n >= 2 ? Math.min(9, Math.round(n)) : 2;
  });
  const [pricePerLiter, setPricePerLiter] = useState(search.pl ?? "5,89");
  const [kmPerLiter, setKmPerLiter] = useState(search.kml ?? "10");
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const [scrollPending, setScrollPending] = useState(false);
  const [markup, setMarkup] = useState(search.mk ?? "1,00");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [legs, setLegs] = useState<LegItem[]>([]);
  const [copied, setCopied] = useState(false);
  // Muda a cada execução bem-sucedida: força o mapa a reenquadrar a rota,
  // mesmo quando a nova rota é idêntica à anterior.
  const [fitNonce, setFitNonce] = useState(0);

  // Rola até os resultados somente depois que eles são montados no DOM.
  useEffect(() => {
    if (!scrollPending || legs.length === 0 || !resultsRef.current) return;
    setScrollPending(false);
    const el = resultsRef.current;
    requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "start" }));
  }, [scrollPending, legs]);


  const vehicleMeta = VEHICLES.find((v) => v.value === vehicle)!;
  const num = (v: string) => Number(v.replace(/\./g, "").replace(",", ".")) || 0;

  // Modo "tarifa por km": quando não há preço/litro nem km/litro, o multiplicador
  // de repasse passa a valer como R$/km direto (custo = km × multiplicador).
  const fuelInputsEmpty = num(pricePerLiter) === 0 && num(kmPerLiter) === 0;
  const markupFactor = num(markup) || 1;
  const perKmRate = fuelInputsEmpty ? markupFactor : 0;

  const costOpts = {
    pricePerLiter: num(pricePerLiter),
    kmPerLiter: num(kmPerLiter),
    vehicle,
    axles,
    manualToll: null,
    perKmRate,
  };

  const legCosts = legs.map((l) => legCost(l.result.outbound, costOpts));
  const totalDistance = legCosts.reduce((s, c) => s + c.distanceKm, 0);
  const totalDuration = legCosts.reduce((s, c) => s + c.durationSec, 0);
  const totalFuel = legCosts.reduce((s, c) => s + c.fuelBrl, 0);
  const totalToll = legCosts.reduce((s, c) => s + (c.tollBrl ?? 0), 0);
  const tollUnavailable = legCosts.length > 0 && legCosts.every((c) => c.tollBrl === null);
  const total = legCosts.reduce((s, c) => s + c.totalBrl, 0);

  const allPlazas = legs.flatMap((l) => l.result.outbound.tollPlazas ?? []);

  const costPerKm = totalDistance > 0 ? total / totalDistance : 0;
  // No modo tarifa por km, o multiplicador já está embutido no combustível;
  // não aplicamos margem extra sobre o total.
  const chargePerKm = fuelInputsEmpty ? costPerKm : costPerKm * markupFactor;
  const chargeTotal = fuelInputsEmpty ? total : total * markupFactor;

  const filledStops = stops.filter((s) => s.address.trim());
  const routeLabel = legs.length
    ? [legs[0]!.result.originLabel, ...legs.filter((l) => !l.isReturn).map((l) => l.result.destinationLabel)]
        .join(" → ")
    : "";

  function updateStop(index: number, value: PlaceValue) {
    setStops((prev) =>
      prev.map((s, i) => (i === index ? { key: s.key, address: value.address, ...(value.placeId ? { placeId: value.placeId } : {}) } : s)),
    );
  }

  function addStop() {
    setStops((prev) => (prev.length >= MAX_STOPS ? prev : [...prev, newStop()]));
  }

  function removeStop(index: number) {
    setStops((prev) => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));
  }

  function moveStop(index: number, direction: -1 | 1) {
    setStops((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const a = next[index]!;
      const b = next[target]!;
      next[index] = b;
      next[target] = a;
      return next;
    });
  }

  /** Limpa a pesquisa atual (rota, erro e parâmetros da URL) para começar de novo. */
  function clearRoute() {
    setLegs([]);
    setError(null);
    setCopied(false);
    setStops([newStop(), newStop()]);
    void navigate({ to: "/", search: {}, replace: true });
    if (typeof window !== "undefined") {
      requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }
  }

  function urlSearch(list: Stop[] = stops, rt = roundTrip, v = vehicle, ax = axles): HomeSearch {
    return {
      r: list
        .filter((s) => s.address.trim())
        .map((s) => s.address.trim())
        .join("|"),
      rt: rt ? "1" : "0",
      v,
      ax: String(ax),
      pl: pricePerLiter,
      kml: kmPerLiter,
      mk: markup,
    };
  }

  async function runSearch(
    list: Stop[] = stops,
    rt: boolean = roundTrip,
    v: VehicleType = vehicle,
    ax: number = axles,
  ) {
    const pts = list.filter((s) => s.address.trim());
    if (pts.length < 2) {
      setError("Preencha ao menos uma origem e um destino.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { computeRoute } = await import("@/lib/rota.functions");
      const meta = VEHICLES.find((x) => x.value === v)!;
      const pairs: [Stop, Stop][] = [];
      for (let i = 0; i < pts.length - 1; i += 1) pairs.push([pts[i]!, pts[i + 1]!]);
      if (rt) pairs.push([pts[pts.length - 1]!, pts[0]!]);

      const visitorId = getVisitorId();
      const results = await Promise.all(
        pairs.map(([a, b]) =>
          computeRoute({
            data: {
              origin: { address: a.address, ...(a.placeId ? { placeId: a.placeId } : {}) },
              destination: { address: b.address, ...(b.placeId ? { placeId: b.placeId } : {}) },
              roundTrip: false,
              motorcycle: v === "moto",
              vehicle: v,
              axles: meta.axles ? ax : 2,
              visitorId,
            },
          }),
        ),
      );

      setLegs(results.map((res, i) => ({ result: res, isReturn: rt && i === pairs.length - 1 })));
      setFitNonce((n) => n + 1);
      void track("search", {
        origin: pts[0]!.address,
        destination: pts[pts.length - 1]!.address,
      });
      // Mobile: rola direto para o resultado do cálculo (após a renderização).
      if (typeof window !== "undefined" && window.innerWidth < 1024) {
        setScrollPending(true);
      }
    } catch (e) {
      setLegs([]);
      setError(e instanceof Error ? e.message : "Não foi possível calcular a rota.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    void navigate({ to: "/", search: urlSearch(), replace: true });
    await runSearch();
  }

  async function copyLink() {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(
      Object.entries(urlSearch()).filter(([, v]) => Boolean(v)) as [string, string][],
    );
    const url = `${window.location.origin}/?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copie o link da pesquisa:", url);
    }
  }

  // Pesquisa compartilhada: se a URL já vem com a rota montada, calculamos sozinho.
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    if (parseStops(search.r).length >= 2) void runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const polylines = useMemo(
    () => legs.map((l) => l.result.outbound.polyline).filter((p): p is string => Boolean(p)),
    [legs],
  );
  // Os marcadores saem da própria geometria da rota (início de cada trecho de
  // ida + destino final), então funcionam mesmo com resultado vindo do cache.
  const outboundCount = legs.filter((l) => !l.isReturn).length;
  const stopLabels = useMemo(
    () =>
      legs.length
        ? [
            legs[0]!.result.originLabel,
            ...legs.filter((l) => !l.isReturn).map((l) => l.result.destinationLabel),
          ]
        : [],
    [legs],
  );



  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="sticky top-0 z-30 h-16 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="flex h-full items-center gap-4 px-4">
          <div className="flex shrink-0 items-center gap-2">
            <img
              src={logoMark}
              alt="Logo TrajetoFácil"
              width={36}
              height={36}
              fetchPriority="high"
              className="size-9 rounded-xl"
            />

            <div className="hidden sm:block">
              <p className="text-lg font-extrabold leading-none tracking-tight text-foreground">TrajetoFácil</p>
              <p className="text-xs text-muted-foreground">roteirizador grátis: rota, pedágio e combustível</p>
            </div>
          </div>
          <FeedbackLauncher className="ml-auto shrink-0" />
        </div>
      </header>

      {/* Introdução — largura total acima da área do app */}
      <main className="flex-1">
      <div className="w-full px-4 pt-6 sm:px-6 lg:px-8">
        <h1 className="text-xl font-extrabold tracking-tight text-foreground sm:text-2xl">
          Roteirizador grátis de rota, pedágio e combustível
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Informe origem e destino e veja em segundos a distância, o tempo, o gasto de combustível e o
          pedágio de cada sentido — para carro, moto, ônibus ou caminhão, com ida e volta e sem cadastro.
        </p>
      </div>

      {/* Área do app: formulário + resumo (esquerda) + mapa (direita) */}
      <div className="flex flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-stretch lg:px-8">
        {/* Formulário */}
        <div className="w-full space-y-4 rounded-2xl border border-border bg-card p-4 shadow-sm lg:w-[400px] lg:shrink-0">
          {stops.map((stop, index) => (
            <PlaceInput
              key={stop.key}
              label={
                index === 0 ? "Origem" : index === stops.length - 1 ? "Destino" : `Parada ${index}`
              }
              placeholder="CEP, cidade, estado ou endereço"
              value={stop}
              onChange={(value) => updateStop(index, value)}
              actions={
                <>
                  <StopAction
                    label="Mover para cima"
                    disabled={index === 0}
                    onClick={() => moveStop(index, -1)}
                  >
                    <ArrowUp className="size-3.5" />
                  </StopAction>
                  <StopAction
                    label="Mover para baixo"
                    disabled={index === stops.length - 1}
                    onClick={() => moveStop(index, 1)}
                  >
                    <ArrowDown className="size-3.5" />
                  </StopAction>
                  <StopAction
                    label="Remover parada"
                    disabled={stops.length <= 2}
                    onClick={() => removeStop(index)}
                  >
                    <Trash2 className="size-3.5" />
                  </StopAction>
                </>
              }
            />
          ))}

          {(legs.length > 0 || stops.some((s) => s.address.trim())) && (
            <div className="-mt-2 flex justify-end">
              <button
                type="button"
                onClick={clearRoute}
                className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground"
              >
                <X className="size-3.5" /> Limpar rota
              </button>
            </div>
          )}


          <div className="flex items-center justify-between gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 flex-1"
              onClick={addStop}
              disabled={stops.length >= MAX_STOPS}
            >
              <Plus className="size-4" /> Adicionar destino
            </Button>
            <span className="text-xs text-muted-foreground">
              {stops.length}/{MAX_STOPS}
            </span>
          </div>


          <div className="flex items-center justify-between rounded-xl bg-muted px-3 py-2.5">
            <span className="flex items-center gap-2 text-sm font-medium text-foreground">
              <ArrowLeftRight className="size-4 text-primary" /> Ida e volta
            </span>
            <Switch
              aria-label="Calcular ida e volta"
              checked={roundTrip}
              onCheckedChange={setRoundTrip}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Veículo
              </Label>
              <NativeSelect
                aria-label="Tipo de veículo"
                className="mt-1.5"
                value={vehicle}
                onChange={(event) => {
                  const next = event.target.value as VehicleType;
                  setVehicle(next);
                  const meta = VEHICLES.find((x) => x.value === next);
                  if (meta) setKmPerLiter(String(meta.defaultKmL).replace(".", ","));
                }}
              >
                {VEHICLES.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </NativeSelect>
            </div>
            {vehicleMeta.axles && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Eixos
                </Label>
                <NativeSelect
                  aria-label="Número de eixos"
                  className="mt-1.5"
                  value={String(axles)}
                  onChange={(event) => setAxles(Number(event.target.value))}
                >
                  {[2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <option key={n} value={String(n)}>{n} eixos</option>
                  ))}
                </NativeSelect>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label
                htmlFor="preco"
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                R$ por litro
              </Label>
              <Input
                id="preco"
                inputMode="decimal"
                className="mt-1.5 h-12 text-base"
                value={pricePerLiter}
                onChange={(e) => setPricePerLiter(e.target.value)}
              />
            </div>
            <div>
              <Label
                htmlFor="consumo"
                className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
              >
                KM por litro
              </Label>
              <Input
                id="consumo"
                inputMode="decimal"
                className="mt-1.5 h-12 text-base"
                value={kmPerLiter}
                onChange={(e) => setKmPerLiter(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label
              htmlFor="repasse"
              className="text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {fuelInputsEmpty ? "Valor por km (R$)" : "Multiplicador de repasse"}
            </Label>
            <Input
              id="repasse"
              inputMode="decimal"
              className="mt-1.5 h-12 text-base"
              value={markup}
              onChange={(e) => setMarkup(e.target.value)}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {fuelInputsEmpty
                ? "Sem preço/litro e km/litro, este valor vira a tarifa por km (ex.: 2,50 = R$ 2,50 por km)."
                : "Use 1,00 para repassar o custo puro; 1,30 adiciona 30% de margem."}
            </p>
          </div>


          <Button
            className="h-13 w-full py-3.5 text-base font-bold"
            onClick={handleSearch}
            disabled={loading}
          >
            {loading ? <Loader2 className="size-5 animate-spin" /> : <Search className="size-5" />}
            {loading ? "Calculando..." : "Buscar rota"}
          </Button>

          {error && (
            <p className="rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          {/* Anúncio 300x250 logo abaixo do botão Buscar rota */}
          <AdSlot className="h-[250px] w-full" minHeight={250} slot="9339379488" />
        </div>

        {/* Resumo dos custos — coluna à esquerda do mapa */}
        {legs.length > 0 ? (
          <div
            ref={resultsRef}
            className="w-full scroll-mt-20 space-y-3 lg:w-[300px] lg:shrink-0 lg:overflow-y-auto"
          >

            <div className="rounded-2xl border border-border bg-primary p-4 text-primary-foreground shadow-sm">

              <p className="text-xs font-semibold uppercase tracking-widest opacity-80">
                Custo total da viagem
              </p>
              <p className="mt-1 text-3xl font-extrabold tracking-tight">{formatBrl(total)}</p>
              <p className="mt-1 text-xs opacity-90">
                {routeLabel}
                {roundTrip ? " (ida e volta)" : ""}
              </p>
              <p className="mt-1 text-xs opacity-80">
                {formatKm(totalDistance)} no total · {formatBrl(costPerKm)} por km
              </p>

              <dl className="mt-3 space-y-2 text-sm">
                <RowOnPrimary label="Distância somada" value={formatKm(totalDistance)} />
                <RowOnPrimary label="Tempo estimado" value={formatDuration(totalDuration)} />
                <RowOnPrimary label="Combustível" value={formatBrl(totalFuel)} />
                <RowOnPrimary
                  label="Pedágio (estimativa)"
                  value={tollUnavailable ? "Indisponível" : formatBrl(totalToll)}
                />
              </dl>

              <CostChart tone="onPrimary" fuelBrl={totalFuel} tollBrl={totalToll} />

              {allPlazas.length > 0 && (
                <details className="mt-3 rounded-xl bg-primary-foreground/10 p-3 text-xs">
                  <summary className="cursor-pointer font-semibold">
                    Praças de pedágio da viagem ({allPlazas.length})
                  </summary>
                  <ul className="mt-2 space-y-1">
                    {allPlazas.map((plaza, i) => (
                      <li key={`${plaza.code}-${i}`} className="flex items-baseline justify-between gap-3">
                        <span className="truncate opacity-90">
                          {plaza.name}
                          {plaza.ref ? ` · ${plaza.ref}` : ""}
                        </span>
                        <span className="font-semibold">{formatBrl(plaza.priceBrl)}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <button
                type="button"
                onClick={copyLink}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-foreground/15 px-3 py-2 text-xs font-semibold transition hover:bg-primary-foreground/25"
              >
                <Link2 className="size-3.5" />
                {copied ? "Link copiado!" : "Copiar link da pesquisa"}
              </button>
            </div>

            {!fuelInputsEmpty && (
              <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Valor a repassar ({markupFactor.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}x)
                </p>
                <p className="mt-1 text-2xl font-extrabold tracking-tight text-foreground">
                  {formatBrl(chargeTotal)}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatBrl(chargePerKm)} por km · margem de {formatBrl(chargeTotal - total)}
                </p>
              </div>
            )}

            {legs.map((leg, i) => (
              <LegCard
                key={`${leg.result.originLabel}-${i}`}
                title={leg.isReturn ? "Volta" : legs.length > (roundTrip ? 2 : 1) ? `Trecho ${i + 1}` : "Ida"}
                subtitle={`${leg.result.originLabel} → ${leg.result.destinationLabel}`}
                cost={legCosts[i]!}
                plazas={leg.result.outbound.tollPlazas ?? []}
              />
            ))}
          </div>
        ) : null}

        {/* Mapa — ao lado, altura igual à da coluna do formulário */}
        <div className="relative isolate z-0 min-h-[400px] flex-1 overflow-hidden rounded-2xl border border-border bg-muted/30 lg:min-h-0">
          <ClientOnly fallback={<div className="h-full w-full animate-pulse bg-muted" />}>
            <DeferredMap polylines={polylines} labels={stopLabels} outboundCount={outboundCount} fitNonce={fitNonce} />
          </ClientOnly>
        </div>
      </div>

      {legs.length > 0 ? (

        <div className="w-full px-4 sm:px-6 lg:px-8">
          <AdSlot minHeight={100} />
        </div>
      ) : null}

      {/* Seções de conteúdo — largura total da tela */}
      <div className="w-full px-4 py-10 sm:px-6 lg:px-8">
        <section aria-labelledby="como-funciona" className="mt-10">
          <h2 id="como-funciona" className="text-center text-lg font-extrabold tracking-tight text-foreground sm:text-xl">
            Como funciona o roteirizador
          </h2>
          <ol className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-6">
            {HOW_TO_STEPS.map((step, i) => (
              <li key={step.name} className="flex flex-col gap-2 rounded-2xl border border-border bg-card p-4 shadow-sm">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                  {i + 1}
                </span>
                <span className="text-sm text-muted-foreground">
                  <strong className="block font-semibold text-foreground">{step.name}</strong>
                  {step.text}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="conteudos" className="mt-12">
          <h2 id="conteudos" className="text-center text-lg font-extrabold tracking-tight text-foreground sm:text-xl">
            Entenda o cálculo e veja exemplos
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {CONTENT_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group rounded-2xl border border-border bg-card p-5 shadow-sm transition hover:border-primary/50"
              >
                <span className="block text-sm font-bold text-foreground group-hover:text-primary">
                  {item.title}
                </span>
                <span className="mt-1.5 block text-sm text-muted-foreground">{item.text}</span>
              </Link>
            ))}
          </div>
        </section>

        <section aria-labelledby="faq" className="mt-12">
          <h2 id="faq" className="text-center text-lg font-extrabold tracking-tight text-foreground sm:text-xl">
            Perguntas frequentes
          </h2>
          <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FAQ.map((item) => (
              <div key={item.q} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <dt className="text-sm font-bold text-foreground">{item.q}</dt>
                <dd className="mt-1.5 text-sm text-muted-foreground">{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
      </main>

      <footer className="border-t border-border px-4 py-6 text-center text-xs text-muted-foreground">
        <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
          <Link to="/como-funciona" className="transition hover:text-foreground">Como funciona</Link>
          <Link to="/metodologia" className="transition hover:text-foreground">Metodologia</Link>
          <Link to="/recursos" className="transition hover:text-foreground">Recursos</Link>
          <Link to="/guias" className="transition hover:text-foreground">Guias</Link>
          <Link to="/casos-de-uso" className="transition hover:text-foreground">Casos de uso</Link>
          <Link to="/sobre" className="transition hover:text-foreground">Sobre</Link>
          <Link to="/contato" className="transition hover:text-foreground">Contato</Link>
          <Link to="/privacidade" className="transition hover:text-foreground">Privacidade</Link>
          <Link to="/termos" className="transition hover:text-foreground">Termos de uso</Link>
        </nav>
        <p className="mt-2">TrajetoFácil · valores de pedágio e combustível são estimativas</p>
      </footer>
    </div>
  );
}

/**
 * O mapa volta a aparecer automaticamente como antes, mas só é montado depois
 * do `load` da página + um momento ocioso. Assim os tiles do OpenStreetMap
 * nunca disputam o LCP no carregamento inicial.
 */
function DeferredMap({
  polylines,
  labels,
  outboundCount,
  fitNonce,
}: {
  polylines: string[];
  labels: string[];
  outboundCount: number;
  fitNonce: number;
}) {

  const [ready, setReady] = useState(false);
  const hasRoute = polylines.length > 0;
  const shouldRender = ready || hasRoute;


  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    let timer = 0;
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    const events = ["pointerdown", "keydown", "touchstart"] as const;

    const mount = () => {
      if (cancelled) return;
      cancelled = true;
      cleanup();
      setReady(true);
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, mount));
    };

    const schedule = () => {
      if (cancelled) return;
      // O mapa aparece sozinho logo após o carregamento da página, no primeiro
      // momento ocioso — fica fora da janela crítica de LCP/TBT, sem exigir
      // interação. Qualquer interação também o monta imediatamente.
      events.forEach((e) => window.addEventListener(e, mount, { once: true, passive: true }));
      const ric = (window as any).requestIdleCallback as
        | ((cb: () => void, opts?: { timeout: number }) => number)
        | undefined;
      if (ric) ric(mount, { timeout: isDesktop ? 1200 : 1500 });
      else timer = window.setTimeout(mount, 600);
    };

    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
    return () => {
      cancelled = true;
      cleanup();
      window.removeEventListener("load", schedule);
    };
  }, [ready]);

  return (
    <div className="h-full min-h-[280px] w-full">
      {shouldRender ? (
        <Suspense fallback={<div className="h-full w-full animate-pulse bg-muted" />}>
          <RouteMap polylines={polylines} labels={labels} outboundCount={outboundCount} fitNonce={fitNonce} />
        </Suspense>
      ) : (
        <div className="h-full w-full animate-pulse bg-muted" aria-hidden />
      )}
    </div>
  );
}


function LegCard({
  title,
  subtitle,
  cost,
  plazas,
}: {
  title: string;
  subtitle: string;
  cost: ReturnType<typeof legCost>;
  plazas: TollPlazaHit[];
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-foreground">{title}</h2>
        <span className="text-lg font-extrabold text-foreground">{formatBrl(cost.totalBrl)}</span>
      </div>
      <p className="mt-0.5 truncate text-xs text-muted-foreground">{subtitle}</p>
      <dl className="mt-3 space-y-2 text-sm">
        <Row label="Distância" value={formatKm(cost.distanceKm)} />
        <Row label="Tempo estimado" value={formatDuration(cost.durationSec)} />
        <Row label="Combustível" value={formatBrl(cost.fuelBrl)} />
        <Row
          label="Pedágio (estimativa)"
          value={cost.tollBrl === null ? "Indisponível" : formatBrl(cost.tollBrl)}
        />
      </dl>
      {plazas.length > 0 && (
        <details className="mt-3 rounded-xl bg-muted/40 p-3 text-xs">
          <summary className="cursor-pointer font-semibold text-foreground">
            Praças de pedágio ({plazas.length})
          </summary>
          <ul className="mt-2 space-y-1">
            {plazas.map((plaza, i) => (
              <li key={`${plaza.code}-${i}`} className="flex items-baseline justify-between gap-3">
                <span className="truncate text-muted-foreground">
                  {plaza.name}
                  {plaza.ref ? ` · ${plaza.ref}` : ""}
                </span>
                <span className="font-semibold text-foreground">{formatBrl(plaza.priceBrl)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-1.5 last:border-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-semibold text-foreground">{value}</dd>
    </div>
  );
}

/** Linha do resumo dentro do cartão verde (fundo primary). */
function RowOnPrimary({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-primary-foreground/20 pb-1.5 last:border-0">
      <dt className="opacity-90">{label}</dt>
      <dd className="font-semibold">{value}</dd>
    </div>
  );
}

/** Botão pequeno de reordenar/remover parada. */
function StopAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid size-7 place-items-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}
